import { Box, Chip, Stack } from '@atgfr8/atg-ui-tailwind';
import { MenuDownIcon, ShieldAlertIcon } from '@atgfr8/icons';
import { createEffect, createMemo, createSignal, Show } from 'solid-js';

import { AlertCard } from './AlertCard';
import classes from './classes';
import type { AlertsSectionProps, SystemAlert } from './types';

const byPriority = (a: SystemAlert, b: SystemAlert) => a.priority - b.priority;

const sameAlert = (a: SystemAlert, b: SystemAlert) =>
  a.severity === b.severity &&
  a.priority === b.priority &&
  a.title === b.title &&
  a.message === b.message;

export const AlertsSection = (props: AlertsSectionProps) => {
  // Reuse the previous object for an unchanged key so <For> keeps existing DOM
  // (avoids row flicker + interactive rows losing state on list rebuilds).
  const cache = new Map<string, SystemAlert>();
  const stabilize = (list: SystemAlert[]): SystemAlert[] => {
    const seen = new Set<string>();
    const result = list.map((alert) => {
      seen.add(alert.key);
      const prev = cache.get(alert.key);
      if (prev && sameAlert(prev, alert)) return prev;
      cache.set(alert.key, alert);
      return alert;
    });
    for (const key of [...cache.keys()]) {
      if (!seen.has(key)) cache.delete(key);
    }
    return result;
  };

  const stableAlerts = createMemo(() => stabilize(props.alerts));
  const notices = createMemo(() =>
    stableAlerts()
      .filter((a) => a.severity === 'notice')
      .sort(byPriority),
  );
  const blocks = createMemo(() =>
    stableAlerts()
      .filter((a) => a.severity === 'block')
      .sort(byPriority),
  );
  const warnings = createMemo(() =>
    stableAlerts()
      .filter((a) => a.severity === 'warning')
      .sort(byPriority),
  );
  const noticeCount = () => notices().length;
  const blockCount = () => blocks().length;
  const warningCount = () => warnings().length;
  const hasAny = () => noticeCount() + blockCount() + warningCount() > 0;

  // Expanded by default when there's something actionable (a blocker or a
  // notice); re-evaluated when that count transitions. User toggles are
  // respected until then.
  const actionableCount = () => blockCount() + noticeCount();
  const [expanded, setExpanded] = createSignal(actionableCount() > 0);
  createEffect((prev: number | undefined) => {
    const count = actionableCount();
    if (prev !== undefined && prev !== count) {
      setExpanded(count > 0);
    }
    return count;
  }, actionableCount());

  const toggle = () => setExpanded((prev) => !prev);

  return (
    <Show when={hasAny()}>
      <Stack
        direction="column"
        class={classes.card}
        dataTestId={props.dataTestId ?? 'system-alerts'}
      >
        <Stack direction="row" class={classes.headingRow}>
          <ShieldAlertIcon style={{ color: '#b00020' }} />
          <Stack direction="column" class={classes.headingTextCol}>
            <div class={classes.headingTitle}>{props.heading}</div>
            <Show when={blockCount() > 0}>
              <div class={classes.summary}>
                {blockCount()}{' '}
                {blockCount() === 1 ? 'blocking issue' : 'blocking issues'} must
                be resolved before this carrier can be assigned and/or rate con
                can be sent.
              </div>
            </Show>
          </Stack>
        </Stack>

        <Stack direction="column" class={classes.section}>
          <div
            class={classes.sectionHeader}
            role="button"
            tabindex={0}
            aria-expanded={expanded()}
            onClick={toggle}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggle();
              }
            }}
            data-testid="system-alerts-toggle"
          >
            <span class={classes.sectionTitle}>{props.sectionTitle}</span>
            <Stack direction="row" class={classes.pillsWrap}>
              <Show when={noticeCount() > 0}>
                <Chip
                  id="system-alerts-notices"
                  text={`${noticeCount()} ${noticeCount() === 1 ? 'Notice' : 'Notices'}`}
                  backgroundColor="#026ea1"
                  class={classes.pillText}
                />
              </Show>
              <Show when={blockCount() > 0}>
                <Chip
                  id="system-alerts-blocks"
                  text={`${blockCount()} ${blockCount() === 1 ? 'Block' : 'Blocks'}`}
                  backgroundColor="#b00020"
                  class={classes.pillText}
                />
              </Show>
              <Show when={warningCount() > 0}>
                <Chip
                  id="system-alerts-warnings"
                  text={`${warningCount()} ${warningCount() === 1 ? 'Warning' : 'Warnings'}`}
                  backgroundColor="#ff8f00"
                  class={classes.pillText}
                />
              </Show>
            </Stack>
            <span
              class={classes.chevron}
              style={{ transform: `rotate(${expanded() ? 0 : 180}deg)` }}
            >
              <MenuDownIcon />
            </span>
          </div>

          <Show when={expanded()}>
            <Box class={classes.content}>
              <AlertCard severity="notice" alerts={notices()} />
              <AlertCard severity="block" alerts={blocks()} />
              <AlertCard severity="warning" alerts={warnings()} />
            </Box>
          </Show>
        </Stack>
      </Stack>
    </Show>
  );
};
